import { CommonModule } from "@angular/common";
import { Component, OnInit, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { RadioButtonModule } from "primeng/radiobutton";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { SkeletonModule } from "primeng/skeleton";
import { UiToastService } from "../../../shared/components/toast/ui-toast.service";
import { BqaRole, ProfileSummary, RoleOption, RoleSwitchService } from "../services/role-switch.service";
@Component({
  standalone: true,
  selector: "app-my-profile",
  imports: [
    CommonModule,
    FormsModule,
    RadioButtonModule,
    SkeletonModule,
    ProgressSpinnerModule
  ],
  templateUrl: "./my-profile.component.html",
  styleUrl: "./my-profile.component.scss"
})
export class MyProfileComponent implements OnInit {
  BqaRole = BqaRole;

  roles: RoleOption[] = [];

  readonly profile = signal<ProfileSummary>({
    userName: ""
  });

  readonly isProfileLoading = signal(true);
  readonly activeRole = signal<BqaRole>(BqaRole.CustomerCareManager);
  readonly selectedRole = signal<BqaRole>(BqaRole.CustomerCareManager);
  readonly isSwitching = signal(false);

  constructor(
    private router: Router,
    private roleSwitchService: RoleSwitchService,
    private toastService: UiToastService
  ) { }

  async ngOnInit(): Promise<void> {
    const role = this.roleSwitchService.getRoleFromUrl(this.router.url);
    this.activeRole.set(role);
    this.selectedRole.set(role);
    this.roles = this.roleSwitchService.roleOptions;
    await this.loadProfileSummary();
  }

  isRoleDisabled(role: BqaRole): boolean {
    return !this.roleSwitchService.hasRole(role) || this.isSwitching();
  }

  async onRoleSelected(role: BqaRole): Promise<void> {
    if (role === this.activeRole()) {
      this.selectedRole.set(role);
      return;
    }

    if (!this.roleSwitchService.hasRole(role)) {
      this.selectedRole.set(this.activeRole());
      this.toastService.error("You do not have access to that role");
      return;
    }

    const roleOption = this.roleSwitchService.getRoleOption(role);
    this.selectedRole.set(role);
    this.isSwitching.set(true);

    try {
      const navigated = await this.router.navigate([roleOption.route]);

      if (navigated) {
        this.activeRole.set(role);
        this.toastService.success(roleOption.successMessage);
        return;
      }

      this.selectedRole.set(this.activeRole());
      this.toastService.error(`Unable to switch to ${roleOption.label}`);
    } catch {
      this.selectedRole.set(this.activeRole());
      this.toastService.error(`Unable to switch to ${roleOption.label}`);
    } finally {
      this.isSwitching.set(false);
    }
  }

  private async loadProfileSummary(): Promise<void> {
    this.isProfileLoading.set(true);

    try {
      this.profile.set(await this.roleSwitchService.getProfileSummary());
    } catch {
      this.profile.set({
        userName: "User name not available"
      });
      this.toastService.error("Unable to load profile details");
    } finally {
      this.isProfileLoading.set(false);
    }
  }
}